/*
 * Sourced from https://www.shadcnblocks.com/block/footer7
 * See docs/shadcn-registry.md for more
 */

import { FaFacebook, FaInstagram, FaLinkedin } from 'react-icons/fa'
import Image from 'next/image'

interface FooterProps {
    logo?: {
        url: string
        src: string
        alt: string
        title: string
        width: number
        height: number
    }
    sections?: {
        title: string
        links: { name: string; href: string }[]
    }[]
    description?: string
    socialLinks?: {
        icon: React.ReactElement
        href: string
        label: string
    }[]
    copyright?: string
    legalLinks?: {
        name: string
        href: string
    }[]
    build?: {
        version: string
        number: string
    }
}

const defaultSections = [
    {
        title: 'Travel',
        links: [
            { name: 'Bus Tickets', href: '/en-us/bus-tickets/' },
            { name: 'Train Tickets', href: '/en-us/train-tickets/' },
        ],
    },
    {
        title: 'About',
        links: [
            { name: 'About Us', href: '/en-us/about-us/' },
            { name: 'Blog', href: 'https://www.wanderu.com/blog/' },
            { name: 'Press', href: 'https://www.wanderu.com/blog/press/' },
            { name: 'Careers', href: '/en-us/about-us/careers/' },
        ],
    },
    {
        title: 'Customer Service',
        links: [
            { name: 'Contact Us', href: 'https://help.wanderu.com/' },
            { name: 'Companies', href: '/en-us/carrier/' },
        ],
    },
]

const defaultSocialLinks = [
    {
        icon: <FaInstagram className="size-5" />,
        href: 'https://instagram.com/wanderu',
        label: 'Instagram',
    },
    {
        icon: <FaFacebook className="size-5" />,
        href: 'https://facebook.com/GoWanderu',
        label: 'Facebook',
    },
    {
        icon: <FaLinkedin className="size-5" />,
        href: 'https://www.linkedin.com/organization-guest/company/wanderu-inc',
        label: 'LinkedIn',
    },
]

const defaultLegalLinks = [
    { name: 'Terms and Conditions', href: '/en-us/terms-and-conditions/' },
    { name: 'Privacy Policy', href: '/en-us/privacy-policy/' },
]

export const Footer = ({
    logo = {
        url: '/',
        src: '/next-static/logos/chiku-and-wanderu-horizontal.svg',
        width: 192,
        height: 44,
        alt: 'logo',
        title: 'wanderu.com',
    },
    sections = defaultSections,
    description = 'The simplest way to book bus and train travel',
    socialLinks = defaultSocialLinks,
    copyright = `© ${new Date().getFullYear()} wanderu.com. All rights reserved.`,
    legalLinks = defaultLegalLinks,
    build = {
        version: 'local',
        number: 'local',
    },
}: FooterProps) => {
    return (
        <footer className="w-full pt-24">
            <div className="container mx-auto px-4 md:px-6">
                <div className="flex w-full flex-col justify-between gap-10 lg:flex-row lg:items-start lg:text-left">
                    <div className="flex w-full flex-col justify-between gap-6 lg:flex-[2] lg:items-start">
                        {/* Logo */}
                        <div className="flex items-center gap-2 lg:justify-start">
                            <a href={logo.url}>
                                <Image
                                    src={logo.src}
                                    alt={logo.alt}
                                    width={logo.width}
                                    height={logo.height}
                                    title={logo.title}
                                    className="h-11"
                                />
                            </a>
                        </div>
                        <p className="max-w-[70%] text-sm text-muted-foreground">{description}</p>
                        <ul className="flex items-center space-x-4 text-muted-foreground">
                            {socialLinks.map((social, idx) => (
                                <li key={idx} className="font-medium hover:text-primary">
                                    <a href={social.href} aria-label={social.label}>
                                        {social.icon}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="grid w-full gap-6 md:grid-cols-3 lg:flex-[3] lg:gap-10">
                        {sections.map((section, sectionIdx) => (
                            <div key={sectionIdx}>
                                <h3 className="mb-4 font-bold">{section.title}</h3>
                                <ul className="space-y-3 text-sm text-muted-foreground">
                                    {section.links.map((link, linkIdx) => (
                                        <li
                                            key={linkIdx}
                                            className="font-medium hover:text-primary"
                                        >
                                            <a href={link.href}>{link.name}</a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="mt-8 flex flex-col justify-between gap-4 border-t pt-8 pb-1 text-xs font-medium text-muted-foreground md:flex-row md:items-center md:text-left">
                    <p className="order-2 lg:order-1">{copyright}</p>
                    <ul className="order-1 flex flex-col gap-2 md:order-2 md:flex-row">
                        {legalLinks.map((link, idx) => (
                            <li key={idx} className="hover:text-primary">
                                <a href={link.href}> {link.name}</a>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="pb-8 text-xs text-transparent selection:text-muted-foreground">
                    version {build.version} | build {build.number}
                </div>
            </div>
        </footer>
    )
}
